import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import "./App.css";
import DashboardPage from "./pages/DashboardPage";
import CalendarPage from "./pages/CalendarPage";
import ExpensesPage from "./pages/ExpensesPage";
import InvoicesPage from "./pages/InvoicesPage";
import SentInvoicesPage from "./pages/SentInvoicesPage";
import InvoiceDetailPage from "./pages/InvoiceDetailPage";
import ReceivedInvoicesPage from "./pages/ReceivedInvoicesPage";
import ReceivedInvoiceDetailPage from "./pages/ReceivedInvoiceDetailPage";
import BillingPage from "./pages/BillingPage";
import OnboardingPage from "./pages/OnboardingPage";
import FiscalProfilePage from "./pages/FiscalProfilePage";
import FinancialProfilePage from "./pages/FinancialProfilePage";
import LoginPage from "./pages/LoginPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import FiscalSetupPage from "./pages/FiscalSetupPage";
import IngresosPage from "./pages/IngresosPage";
import ComingSoonPage from "./pages/ComingSoonPage";
import MiBovedaPage from "./pages/MiBovedaPage";
import ClientesPage from "./pages/ClientesPage";
import BusinessProfilePage from "./pages/BusinessProfilePage";
import MyProfilePage from "./pages/MyProfilePage";
import CashPage from "./pages/CashPage";
import SuppliersPage from "./pages/SuppliersPage";
import DeclarationsPage from "./pages/DeclarationsPage";
import NotesPage from "./pages/NotesPage";
import InventoryPage from "./pages/InventoryPage";
import InventoryDetailPage from "./pages/InventoryDetailPage";
import ProductsPage from "./pages/ProductsPage";
import ImportPage from "./pages/ImportPage";
import AcademiaPage from "./pages/AcademiaPage";
import AcademiaLessonPage from "./pages/AcademiaLessonPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import PayrollDashboardPage from "./pages/PayrollDashboardPage";
import EmployeeOnboardingPage from "./pages/EmployeeOnboardingPage";
import EmployeeDetailPage from "./pages/EmployeeDetailPage";
import PortfolioPage from "./pages/PortfolioPage";
import AssetDetailPage from "./pages/AssetDetailPage";
import PublicProjectPage from "./pages/PublicProjectPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import PricingPage from "./pages/PricingPage";
import TrialExpiredPage from "./pages/TrialExpiredPage";
import PaymentResultPage from "./pages/PaymentResultPage";
import MiSuscripcionPage from "./pages/MiSuscripcionPage";
import CheckoutPage from "./pages/CheckoutPage";
import { TourProvider } from "./components/TourContext";
import TourOverlay from "./components/TourOverlay";
import { api } from "./lib/api";

function HomeRoute() {
  // Onboarding questionnaire (pre-auth) is temporarily bypassed — new visitors
  // land on the Sign In / Sign Up screen directly. OnboardingPage import is
  // preserved so we can restore this flow later without a code archaeology dig.
  const [authenticated, setAuthenticated] = useState(null);
  useEffect(() => {
    api.get("/auth/me", { withCredentials: true }).then(() => setAuthenticated(true)).catch(() => setAuthenticated(false));
  }, []);
  if (authenticated === null) {
    return (
      <div className="app-loading" data-testid="app-loading-state">
        <img src="/mi-contador-logo.png" alt="Mi Contador" className="app-loading-logo" />
        <span>Preparando Mi Contador…</span>
      </div>
    );
  }
  if (authenticated) return <DashboardPage />;
  return <LoginPage />;
}

export default function App() {
  return (
    <BrowserRouter>
      <TourProvider>
        <TourOverlay />
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/boveda" element={<MiBovedaPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/configuracion-fiscal" element={<FiscalSetupPage />} />
          <Route path="/perfil-negocio" element={<BusinessProfilePage />} />
          <Route path="/configuracion/facturacion" element={<Navigate to="/perfil-negocio?tab=fiscal" replace />} />
          <Route path="/facturacion/setup" element={<Navigate to="/perfil-negocio?tab=fiscal" replace />} />
          <Route path="/mi-perfil" element={<MyProfilePage />} />
          <Route path="/caja" element={<CashPage />} />
          <Route path="/perfil-fiscal" element={<FiscalProfilePage />} />
          <Route path="/perfil-financiero" element={<FinancialProfilePage />} />
          <Route path="/tranquilo" element={<CalendarPage />} />
          <Route path="/calendario" element={<CalendarPage />} />
          <Route path="/ingresos" element={<IngresosPage />} />
          <Route path="/gastos" element={<ExpensesPage />} />
          <Route path="/facturacion" element={<BillingPage />} />
          <Route path="/facturacion/wizard" element={<InvoicesPage />} />
          <Route path="/facturas" element={<SentInvoicesPage />} />
          <Route path="/facturas/:id" element={<InvoiceDetailPage />} />
          <Route path="/facturas-recibidas" element={<ReceivedInvoicesPage />} />
          <Route path="/facturas-recibidas/:id" element={<ReceivedInvoiceDetailPage />} />
          <Route path="/clientes" element={<ClientesPage />} />
          <Route path="/proveedores" element={<SuppliersPage />} />
          <Route path="/declaraciones" element={<DeclarationsPage />} />
          <Route path="/academia" element={<AcademiaPage />} />
          <Route path="/academia/:slug" element={<AcademiaLessonPage />} />
          <Route path="/proyectos" element={<ProjectsPage />} />
          <Route path="/proyectos/:id" element={<ProjectDetailPage />} />
          <Route path="/nomina" element={<PayrollDashboardPage />} />
          <Route path="/nomina/nuevo" element={<EmployeeOnboardingPage />} />
          <Route path="/nomina/:id" element={<EmployeeDetailPage />} />
          <Route path="/patrimonio" element={<PortfolioPage />} />
          <Route path="/patrimonio/:id" element={<AssetDetailPage />} />
          <Route path="/p/:token" element={<PublicProjectPage />} />
          <Route path="/notas" element={<NotesPage />} />
          <Route path="/inventario" element={<InventoryPage />} />
          <Route path="/inventario/:id" element={<InventoryDetailPage />} />
          <Route path="/productos" element={<ProductsPage />} />
          <Route path="/importar" element={<ImportPage />} />
          <Route path="/legal/privacidad" element={<PrivacyPolicyPage />} />
          <Route path="/legal/terminos" element={<TermsPage />} />
          <Route path="/legal/cookies" element={<CookiePolicyPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/trial-expired" element={<TrialExpiredPage />} />
          <Route path="/checkout/success" element={<PaymentResultPage variant="success" />} />
          <Route path="/checkout/pending" element={<PaymentResultPage variant="pending" />} />
          <Route path="/checkout/failure" element={<PaymentResultPage variant="failure" />} />
          <Route path="/checkout/cancelled" element={<PaymentResultPage variant="cancelled" />} />
          <Route path="/mi-suscripcion" element={<MiSuscripcionPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="*" element={<HomeRoute />} />
        </Routes>
      </TourProvider>
    </BrowserRouter>
  );
}